import { CATEGORIES } from '../../constants/categories';
import { SKILL_LEVELS } from '../../constants/actionTypes';
import { validateDefinition } from './validateDefinition';
import type { GpbDefect } from './validateDefinition';
import type { GpbDefinition } from '../../types/gpb';

/**
 * ⭐⭐ **THE PRODUCT CONTRACT A RELEASE MUST MEET — AND A DRAFT NEED NOT.**
 *
 * `validateDefinition` answers "would the engine MISREAD this?" and accepts a
 * one-rank fixture, because a one-rank definition reads perfectly well. It is not
 * the product. A Growth Profile is SEVEN ranks (D6), each ending in its quests, and
 * ONE ability whose seven tiers the ranks climb — rank N requires tier N's skills
 * inside one of its goals, which is how Athlete is authored (rank 1 → foundation's
 * S01/S04/S10 … rank 7 → mastery's S14/S15).
 *
 * ⚠️⚠️ **KEPT APART FROM THE VALIDATOR ON PURPOSE.** The validator's findings are
 * shown on every draft and its tests pin a one-rank fixture; folding these in would
 * either fail those tests or make every unfinished draft look broken. They join it
 * at PUBLICATION only (`publicationFindings`), so saving stays unconditional.
 *
 * ⚠️⚠️ **GAPS STILL DO NOT BLOCK.** Everything here is a `defect`: a release without
 * seven ranks is not "not yet available", it is a different product. The validator's
 * own `gap` severity (untranslated programs — Athlete has nine) is untouched, so the
 * existing publication policy is not quietly tightened.
 *
 * ⚠️ "Required ability-tier completion WITHOUT capping independent advancement":
 * rank N must require ALL of tier N's skills; nothing here forbids a person
 * completing higher tiers early, or a goal requiring more.
 */

const DOMAIN_IDS: ReadonlySet<string> = new Set(CATEGORIES.map(c => c.id));
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
export const REQUIREMENT_TYPES: readonly string[] = ['read', 'watch', 'listen', 'practice', 'create', 'complete'];

const contract = (where: string, message: string): GpbDefect =>
  ({ code: 'contract', severity: 'defect', where, message });

export function contractDefects(def: GpbDefinition): GpbDefect[] {
  const out: GpbDefect[] = [];
  const ranks = def.ranks ?? [];

  /* Seven ranks, numbered 1–7, in order. */
  const numbers = ranks.map(r => r.rank);
  const inOrder = numbers.length === 7 && numbers.every((n, i) => n === i + 1);
  if (!inOrder) {
    out.push(contract('ranks', `a profile publishes exactly seven ranks numbered 1–7 in order; this has ${numbers.length ? numbers.join(', ') : 'none'}.`));
  }

  /* One ability, complete enough to create, with every one of the seven tiers. */
  const ability = def.components?.ability;
  const A = 'components.ability';
  if (!ability || !ability.id?.trim()) out.push(contract(A, 'the profile has no core ability.'));
  else {
    if (!ability.title?.trim()) out.push(contract(A, 'the ability has no title.'));
    if (!ability.description?.trim()) out.push(contract(A, 'the ability has no description — the app requires one.'));
    if (!DOMAIN_IDS.has(ability.domain)) out.push(contract(A, `the ability's domain "${ability.domain}" is not a domain id.`));
  }
  const skills = ability?.skills ?? [];
  for (const s of skills) {
    if (!(SKILL_LEVELS as readonly string[]).includes(s.tier)) {
      out.push(contract(`${A}.skills[${s.id}]`, `skill ${s.id} has tier "${s.tier}", which is not one of the seven tiers.`));
    }
    /* ⚠️ A requirement is created as a native SkillRequirement — it needs a title a
       person can read and one of the native kinds, or it is created broken. */
    const seenReq = new Set<string>();
    for (const r of s.requirements ?? []) {
      const at = `${A}.skills[${s.id}]`;
      if (!r.title?.trim()) out.push(contract(at, `a requirement of skill ${s.id} has no title.`));
      if (!REQUIREMENT_TYPES.includes(r.type)) out.push(contract(at, `requirement ${r.id} of skill ${s.id} has type "${r.type}", which is not a requirement kind.`));
      if (seenReq.has(r.id)) out.push(contract(at, `skill ${s.id} authors requirement ${r.id} twice.`));
      seenReq.add(r.id);
    }
  }
  SKILL_LEVELS.forEach((tier, i) => {
    const ofTier = skills.filter(s => s.tier === tier).map(s => s.id);
    if (!ofTier.length) {
      out.push(contract(A, `tier ${i + 1} (${tier}) has no skills — the ability spans all seven tiers.`));
      return;
    }
    /* Rank i+1 requires every skill of tier i+1, somewhere in its goals. */
    const rank = ranks.find(r => r.rank === i + 1);
    if (!rank) return;
    const required = new Set(rank.quests.flatMap(q => q.goals.flatMap(g =>
      g.all.flatMap(p => (p.kind === 'skills' ? p.ids : [])))));
    const missing = ofTier.filter(id => !required.has(id));
    if (missing.length) {
      out.push(contract(`R${i + 1}`, `rank ${i + 1} does not require tier ${i + 1}'s ${missing.join(', ')} — each rank completes its own tier.`));
    }
  });

  /* Every component executes somewhere real. */
  for (const h of def.components?.habits ?? []) {
    if (h.domain && !DOMAIN_IDS.has(h.domain)) out.push(contract(`components.habits[${h.id}]`, `habit ${h.id}'s domain "${h.domain}" is not a domain id.`));
  }
  for (const p of def.components?.programs ?? []) {
    if (p.domain && !DOMAIN_IDS.has(p.domain)) out.push(contract(`components.programs[${p.id}]`, `program ${p.id}'s domain "${p.domain}" is not a domain id.`));
  }
  for (const p of def.projects ?? []) {
    /* ⚠️ A project used to be created in Personal whatever it was for. A release now
       says where each one executes; Athlete's v0.1 predates the field and keeps the
       Personal default it shipped with (see storeWriter). */
    if (!p.domain) out.push(contract(`projects[${p.id}]`, `project ${p.id} has no execution domain.`));
    else if (!DOMAIN_IDS.has(p.domain)) out.push(contract(`projects[${p.id}]`, `project ${p.id}'s domain "${p.domain}" is not a domain id.`));
  }

  /* The time suggestion, when authored, is well formed — never required (D42's absence branch). */
  const L = def.loadout;
  if (L) {
    if (!DOMAIN_IDS.has(L.dominant)) out.push(contract('loadout', `the dominant domain "${L.dominant}" is not a domain id.`));
    const seen = new Set<string>();
    for (const d of L.domains) {
      const at = `loadout.domains[${d.domain}]`;
      if (!DOMAIN_IDS.has(d.domain)) out.push(contract(at, `"${d.domain}" is not a domain id.`));
      if (seen.has(d.domain)) out.push(contract(at, `${d.domain} is listed twice.`));
      seen.add(d.domain);
      if (!(d.low >= 0) || !(d.high >= d.low)) out.push(contract(at, `the range ${d.low}–${d.high} h is not a range.`));
    }
    if (L.domains.length && !seen.has(L.dominant)) out.push(contract('loadout', 'the dominant domain is not among the emphases.'));
    if (L.sleep && (!(L.sleep.low >= 0) || !(L.sleep.high >= L.sleep.low))) out.push(contract('loadout.sleep', 'the sleep range is not a range.'));
  }
  const day = def.dayTemplate;
  if (day) {
    day.blocks.forEach((b, i) => {
      const at = `dayTemplate.blocks[${i}]`;
      if (!TIME.test(b.from) || !TIME.test(b.to)) out.push(contract(at, `"${b.from}–${b.to}" must be HH:MM times.`));
      if (!b.label?.trim()) out.push(contract(at, 'a block has no label.'));
      if (b.domain && !DOMAIN_IDS.has(b.domain)) out.push(contract(at, `"${b.domain}" is not a domain id.`));
    });
  }

  return out;
}

/**
 * ⭐ Everything that decides whether a version may be PUBLISHED: what the engine
 * would misread, plus the product contract. Defects block; gaps never do.
 */
export function publicationFindings(def: GpbDefinition, publishedVersions?: readonly string[]): readonly GpbDefect[] {
  return [...validateDefinition(def, publishedVersions), ...contractDefects(def)];
}
